import '../../styles/LandingPage/ResearchPillars/SolvencyCapital.css';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function SolvencyCapital() {
  const contentRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const items = contentRef.current?.querySelectorAll('.sc-animate');
    items?.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return (
    <section className="sc-detail-section" ref={contentRef}>
      <div className="sc-detail-container">
        <Link to="/" className="sc-back-link sc-animate">
          <i className="ti ti-arrow-left" />
          Back to Research Pillars
        </Link>

        <div className="sc-detail-header sc-animate">
          <div className="sc-detail-label">Pillar 02</div>
          <div className="sc-detail-icon">
            <i className="ti ti-chart-dots" />
          </div>
          <h1>Solvency & Capital Management</h1>
          <p className="sc-detail-subtitle">
            Economic capital modelling, stress testing, and ORSA implementation tailored to the realities of African insurance markets.
          </p>
        </div>

        <div className="sc-content">
          <div className="sc-block sc-animate">
            <h2>Economic Capital Modelling</h2>
            <p>
              Economic capital provides a risk-based measure of the capital required to ensure solvency over a defined time horizon 
              at a given confidence level. Our research develops internal models and standard formula adaptations that reflect 
              the unique risk profile of African insurers — including currency volatility, sovereign risk concentrations, 
              and limited diversification opportunities. We examine both factor-based and simulation-based approaches, 
              with emphasis on computational feasibility for mid-sized insurers.
            </p>
            <div className="sc-highlight">
              <i className="ti ti-bulb" />
              <span>Key Insight: Currency risk alone can account for 25-40% of total economic capital for insurers with significant USD-denominated liabilities.</span>
            </div>
          </div>

          <div className="sc-block sc-animate">
            <h2>Stress Testing for African Insurers</h2>
            <p>
              Effective stress testing must go beyond regulatory minimums to capture the interconnected risks facing African markets. 
              Our research designs scenario libraries that reflect local macroeconomic conditions — including commodity price shocks, 
              sovereign debt crises, and currency devaluations. We develop reverse stress testing frameworks that identify 
              scenarios which would cause insolvency, helping boards and regulators understand the true vulnerabilities 
              of insurance portfolios in emerging markets.
            </p>
          </div>

          <div className="sc-block sc-animate">
            <h2>ORSA Implementation</h2>
            <p>
              The Own Risk and Solvency Assessment (ORSA) is becoming a regulatory requirement across African jurisdictions. 
              Our research provides practical guidance on implementing ORSA processes, from governance structures and risk appetite 
              frameworks to the integration of strategic planning with solvency assessment. We examine how ORSA can move beyond 
              compliance to become a genuine strategic tool for capital allocation and business planning.
            </p>
          </div>

          <div className="sc-block sc-animate">
            <h2>Capital Optimisation</h2>
            <p>
              Capital efficiency is paramount in markets where cost of capital is high and investment returns are constrained. 
              Our research explores techniques for optimising the capital structure of insurance groups, including reinsurance 
              strategy design, product mix optimisation, and the use of innovative capital instruments. We analyse the trade-offs 
              between capital relief and counterparty risk in reinsurance arrangements, with particular attention to 
              the concentration risk that arises from limited reinsurer options in African markets.
            </p>
          </div>

          <div className="sc-block sc-animate">
            <h2>Current Research Outputs</h2>
            <div className="sc-outputs">
              <div className="sc-output-card">
                <i className="ti ti-file-analytics" />
                <h4>African Stress Scenario Library</h4>
                <span>Technical Paper · 2025</span>
              </div>
              <div className="sc-output-card">
                <i className="ti ti-shield-check" />
                <h4>ORSA Implementation Toolkit</h4>
                <span>Framework & Templates · 2025</span>
              </div>
              <div className="sc-output-card">
                <i className="ti ti-chart-pie" />
                <h4>Economic Capital Calculator</h4>
                <span>Open-source Tool · 2025</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}